// static/js/admin/aichat-monitor.js — AIchat 会话监控
// 会话监控只保留一个独立视图，aichat-monitor-* ID 在文档内唯一。
import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { showToast, formatDate, renderPagination } from './utils.js';
import { getUserRole } from './state.js';

let currentPage = 1;
let currentFilters = {
  search: '',
  roleId: '',
  status: '',
  startDate: '',
  endDate: '',
};
let roleOptions = [];
function $(id) {
  return document.getElementById(id);
}

function createMonitorElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createMonitorStateRow(message, type = 'loading') {
  const cell = createMonitorElement('td', `aichat-monitor-table-state aichat-monitor-table-state-${type}`, message);
  cell.colSpan = 8;
  const row = document.createElement('tr');
  row.appendChild(cell);
  return row;
}

function createDrawerState(message, type = 'loading') {
  return createMonitorElement('div', `aichat-monitor-drawer-state aichat-monitor-drawer-state-${type}`, message);
}

function formatMessagePayload(value, pretty = false) {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value, null, pretty ? 2 : 0);
  } catch {
    return String(value);
  }
}

function buildQuery() {
  const params = new URLSearchParams({ page: currentPage, limit: 20 });
  if (currentFilters.search) params.set('search', currentFilters.search);
  if (currentFilters.roleId) params.set('roleId', currentFilters.roleId);
  if (currentFilters.status) params.set('status', currentFilters.status);
  if (currentFilters.startDate) params.set('startDate', `${currentFilters.startDate} 00:00:00`);
  if (currentFilters.endDate) params.set('endDate', `${currentFilters.endDate} 23:59:59`);
  return params.toString();
}

function setLoading() {
  const tbody = $('aichat-monitor-tbody');
  if (tbody) tbody.replaceChildren(createMonitorStateRow('加载中...'));
}

function setError(msg) {
  const tbody = $('aichat-monitor-tbody');
  if (tbody) {
    const row = createMonitorStateRow(msg, 'error');
    const retry = createMonitorElement('button', 'btn btn-secondary aichat-monitor-retry', '重试');
    retry.type = 'button';
    retry.id = 'aichat-monitor-retry';
    row.firstElementChild.appendChild(document.createTextNode(' '));
    row.firstElementChild.appendChild(retry);
    tbody.replaceChildren(row);
  }
  $('aichat-monitor-retry')?.addEventListener('click', () => loadSessions());
}

function setEmpty() {
  const tbody = $('aichat-monitor-tbody');
  if (tbody) tbody.replaceChildren(createMonitorStateRow('暂无会话记录', 'empty'));
}

async function loadSessions() {
  setLoading();
  try {
    const res = await authFetch(`/api/admin/aichat/sessions?${buildQuery()}`);
    if (!res.ok) throw new Error('加载失败');
    const data = await res.json();
    renderSummary(data.sessions || [], data.total || 0);
    renderPageMeta(data.total || 0, currentPage, 20);
    if (!data.sessions || data.sessions.length === 0) {
      setEmpty();
      renderPagination('aichat-monitor-pagination', data.total || 0, currentPage, 20, (p) => { currentPage = p; loadSessions(); });
      return;
    }
    renderTable(data.sessions);
    renderPagination('aichat-monitor-pagination', data.total, currentPage, 20, (p) => { currentPage = p; loadSessions(); });
  } catch (err) {
    setError(err.message || '加载失败');
  }
}

function renderTable(rows) {
  const tbody = $('aichat-monitor-tbody');
  if (!tbody) return;
  tbody.replaceChildren(...rows.map((s) => {
    const row = document.createElement('tr');
    const user = document.createElement('td');
    user.append(createMonitorElement('div', 'aichat-monitor-session-user', s.userName || s.userEmail || '未知'), createMonitorElement('div', 'aichat-monitor-session-sub', s.userEmail || ''), createMonitorElement('div', 'aichat-monitor-session-sub mono', `sess_${s.id || ''}`));
    row.appendChild(user);
    const role = document.createElement('td'); role.appendChild(createMonitorElement('span', 'aichat-monitor-role-badge', s.roleName || s.roleCode || '-')); row.appendChild(role);
    row.appendChild(createMonitorElement('td', 'mono', s.messageCount || 0));
    const model = document.createElement('td'); model.append(createMonitorElement('div', 'aichat-monitor-model-main', s.model || '-'), createMonitorElement('div', 'aichat-monitor-session-sub', s.provider || '')); row.appendChild(model);
    const status = document.createElement('td'); status.appendChild(createMonitorElement('span', `aichat-monitor-status-badge ${s.status === 'ACTIVE' ? 'aichat-monitor-status-badge-active' : 'aichat-monitor-status-badge-inactive'}`, s.status === 'ACTIVE' ? '活跃' : '已归档')); row.appendChild(status);
    row.appendChild(createMonitorElement('td', 'aichat-monitor-time-cell', formatDate(s.lastActiveAt) || '-'));
    row.appendChild(createMonitorElement('td', 'aichat-monitor-time-cell', formatDate(s.createdAt) || '-'));
    const action = document.createElement('td'); const button = createMonitorElement('button', 'btn btn-secondary btn-sm', '查看'); button.type = 'button'; button.dataset.detailId = String(s.id ?? ''); action.appendChild(button); row.appendChild(action);
    return row;
  }));

  tbody.querySelectorAll('[data-detail-id]').forEach(btn => {
    btn.addEventListener('click', () => openDrawer(Number(btn.dataset.detailId)));
  });
}

function renderSummary(rows, total) {
  const activeCount = rows.filter((s) => s.status === 'ACTIVE').length;
  const messageCount = rows.reduce((sum, s) => sum + Number(s.messageCount || 0), 0);
  const avgMessages = rows.length ? (messageCount / rows.length).toFixed(1) : '0';
  setSummaryValue('aichat-monitor-stat-total', formatCompactNumber(total));
  setSummaryValue('aichat-monitor-stat-active', formatCompactNumber(activeCount));
  setSummaryValue('aichat-monitor-stat-messages', formatCompactNumber(messageCount));
  setSummaryValue('aichat-monitor-stat-average', avgMessages);
}

function setSummaryValue(id, value) {
  const el = $(id);
  if (el) el.textContent = String(value);
}

function renderPageMeta(total, page, size) {
  const el = $('aichat-monitor-page-meta');
  if (!el) return;
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / size));
  el.textContent = `第 ${page} 页 / 共 ${totalPages} 页`;
}

function formatCompactNumber(value) {
  const num = Number(value) || 0;
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
  return String(num);
}

async function loadRoles() {
  try {
    const res = await authFetch('/api/admin/chat-roles');
    if (!res.ok) return;
    const data = await res.json();
    roleOptions = data.roles || [];
    const select = $('aichat-monitor-role');
    if (select) {
      select.replaceChildren(new Option('全部角色', ''), ...roleOptions.map((role) => new Option(String(role.name || ''), String(role.id ?? ''))));
    }
  } catch (err) {
    console.warn('[aichat-monitor] load roles failed:', err);
  }
}

function bindEvents() {
  $('aichat-monitor-search-btn')?.addEventListener('click', () => {
    currentFilters.search = $('aichat-monitor-search')?.value?.trim() || '';
    currentFilters.roleId = $('aichat-monitor-role')?.value || '';
    currentFilters.status = $('aichat-monitor-status')?.value || '';
    currentFilters.startDate = $('aichat-monitor-start')?.value || '';
    currentFilters.endDate = $('aichat-monitor-end')?.value || '';
    currentPage = 1;
    loadSessions();
  });
  $('aichat-monitor-reset-btn')?.addEventListener('click', () => {
    $('aichat-monitor-search').value = '';
    $('aichat-monitor-role').value = '';
    $('aichat-monitor-status').value = '';
    $('aichat-monitor-start').value = '';
    $('aichat-monitor-end').value = '';
    currentFilters = { search: '', roleId: '', status: '', startDate: '', endDate: '' };
    currentPage = 1;
    loadSessions();
  });
  $('aichat-monitor-search')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') $('aichat-monitor-search-btn')?.click();
  });
  $('aichat-monitor-export-md')?.addEventListener('click', () => doExport('md'));
  $('aichat-monitor-export-csv')?.addEventListener('click', () => doExport('csv'));

  // 抽屉关闭
  $('aichat-monitor-drawer-close')?.addEventListener('click', closeDrawer);
  $('aichat-monitor-drawer-backdrop')?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });
}

async function doExport(format) {
  const params = new URLSearchParams();
  params.set('format', format);
  if (currentFilters.search) params.set('search', currentFilters.search);
  if (currentFilters.roleId) params.set('roleId', currentFilters.roleId);
  if (currentFilters.status) params.set('status', currentFilters.status);
  if (currentFilters.startDate) params.set('startDate', `${currentFilters.startDate} 00:00:00`);
  if (currentFilters.endDate) params.set('endDate', `${currentFilters.endDate} 23:59:59`);

  showToast(`正在准备 ${format.toUpperCase()} 导出...`, 'info');
  try {
    const res = await authFetch(`/api/admin/aichat/export?${params.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || '导出失败');
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const m = cd.match(/filename="?([^";]+)"?/);
    const filename = m ? m[1] : `aichat-export-${Date.now()}.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast('导出完成', 'success');
  } catch (err) {
    showToast('导出失败: ' + err.message, 'error');
  }
}

async function openDrawer(sessionId) {
  const drawer = $('aichat-monitor-drawer');
  const backdrop = $('aichat-monitor-drawer-backdrop');
  const body = $('aichat-monitor-drawer-body');
  const title = $('aichat-monitor-drawer-title');
  const meta = $('aichat-monitor-drawer-meta');

  if (!drawer || !backdrop || !body) return;
  title.textContent = '会话详情加载中...';
  meta.textContent = '';
  body.replaceChildren(createDrawerState('加载中...'));
  drawer.classList.add('open');
  backdrop.classList.add('open');

  try {
    const res = await authFetch(`/api/admin/aichat/sessions/${sessionId}/messages`);
    if (!res.ok) throw new Error('加载失败');
    const data = await res.json();
    const s = data.session;
    const msgs = data.messages || [];
    title.textContent = s.title || `会话 #${s.id}`;
    meta.textContent = `${s.userName || s.userEmail} · ${s.roleName || s.roleCode} · ${s.provider}/${s.model} · 创建 ${formatDate(s.createdAt)} · ${msgs.length} 条消息`;

    body.replaceChildren(renderMessages(msgs));
    body.scrollTop = 0;
  } catch (err) {
    body.replaceChildren(createDrawerState(err.message || '加载失败', 'error'));
  }
}

function closeDrawer() {
  $('aichat-monitor-drawer')?.classList.remove('open');
  $('aichat-monitor-drawer-backdrop')?.classList.remove('open');
}

function createToolInvocation(tool) {
  const item = createMonitorElement('div', 'aichat-monitor-tool-invocation');
  const title = createMonitorElement('div', 'aichat-monitor-tool-title');
  title.append('🔧 ', String(tool?.tool_code || '未知工具'), ' ');
  title.appendChild(createMonitorElement('span', 'aichat-monitor-tool-status', `(${tool?.status || 'ok'})`));
  item.appendChild(title);

  if (tool?.input !== undefined && tool.input !== null && tool.input !== '') {
    item.appendChild(createMonitorElement('pre', 'aichat-monitor-tool-input', `in: ${formatMessagePayload(tool.input)}`));
  }
  if (tool?.output !== undefined && tool.output !== null && tool.output !== '') {
    const details = createMonitorElement('details', 'aichat-monitor-tool-output');
    details.appendChild(createMonitorElement('summary', '', '查看结果'));
    details.appendChild(createMonitorElement('pre', '', formatMessagePayload(tool.output, true)));
    item.appendChild(details);
  }
  return item;
}

function createDrawerMessage(message = {}) {
  const role = String(message?.role || 'UNKNOWN').toUpperCase();
  const time = formatDate(message?.created_at || message?.createdAt) || '-';
  const roleConfig = {
    USER: { className: 'aichat-monitor-message-user', label: '👤 用户' },
    ASSISTANT: { className: 'aichat-monitor-message-assistant', label: '🤖 助手' },
    SYSTEM: { className: 'aichat-monitor-message-system', label: '⚙️ 系统' },
  }[role] || { className: 'aichat-monitor-message-unknown', label: role || '未知角色' };
  const item = createMonitorElement('article', `aichat-monitor-message ${roleConfig.className}`);
  const meta = createMonitorElement('div', 'aichat-monitor-message-meta', `${roleConfig.label} · ${time}`);
  if (role === 'ASSISTANT' && message?.model) meta.append(` · ${message.model}`);
  const content = createMonitorElement('div', 'aichat-monitor-message-content', message?.content || '');
  item.append(meta, content);

  if (role === 'ASSISTANT' && Array.isArray(message?.tool_invocations) && message.tool_invocations.length) {
    const tools = createMonitorElement('div', 'aichat-monitor-tool-list');
    message.tool_invocations.forEach((tool) => tools.appendChild(createToolInvocation(tool)));
    item.appendChild(tools);
  }
  return item;
}

function renderMessages(messages) {
  const fragment = document.createDocumentFragment();
  const list = Array.isArray(messages) ? messages : [];
  if (!list.length) {
    fragment.appendChild(createDrawerState('无消息', 'empty'));
    return fragment;
  }
  list.forEach((message) => fragment.appendChild(createDrawerMessage(message)));
  return fragment;
}

function setupExportVisibility() {
  // 仅 admin 可见导出按钮
  const isAdmin = getUserRole() === 'admin';
  const md = $('aichat-monitor-export-md');
  const csv = $('aichat-monitor-export-csv');
  if (md) md.hidden = !isAdmin;
  if (csv) csv.hidden = !isAdmin;
}

export function initAichatMonitor() {
  registerViewLoader('admin-aichat-sessions', async () => {
    setupExportVisibility();
    await loadRoles();
    await loadSessions();
  });
  bindEvents();
}
